import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });
export const COMPANY_KEY = "va_company";
api.interceptors.request.use((c) => {
  const cid = localStorage.getItem(COMPANY_KEY);
  if (cid) c.headers["X-Company-Id"] = cid;
  return c;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config || {};
    const skip = /auth\/(login|refresh|invite)/.test(orig.url || "");
    if (err.response?.status === 401 && !orig._retry && !skip) {
      orig._retry = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
      try {
        await refreshing;
        return api(orig);
      } catch {
        window.dispatchEvent(new Event("auth:expired"));
      }
    }
    if (err.response?.status === 403 && err.response?.data?.detail === "No access to this company") {
      localStorage.removeItem(COMPANY_KEY);
      sessionStorage.setItem("company_reset", "1");
      window.location.reload();
      return new Promise(() => {});
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

export async function downloadDoc(docId, version) {
  const { data } = await api.get(`/documents/${docId}/download`, { params: { version }, responseType: "blob" });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const TASK_STATUS = {
  not_started: { label: "Not Started", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  with_authority: { label: "In Review (with Authority)", cls: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  awaiting_client: { label: "Awaiting Client", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  submitted: { label: "Submitted for Review", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  under_review: { label: "Under Review", cls: "bg-teal-50 text-teal-800 border-teal-200" },
  returned: { label: "Returned for Correction", cls: "bg-red-50 text-red-700 border-red-200" },
  verified: { label: "Verified & Completed", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-500 border-gray-200 line-through" },
};
export const TASK_ORDER = ["not_started", "in_progress", "with_authority", "submitted", "under_review", "verified"];

export const DOC_STATUS = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  expiring_soon: { label: "Expiring Soon", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  expired: { label: "Expired", cls: "bg-red-50 text-red-700 border-red-200" },
  needs_attention: { label: "Needs Attention", cls: "bg-orange-50 text-orange-700 border-orange-300" },
  pending_verification: { label: "Pending V.A Verification", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" },
  rejected: { label: "Rejected / Replacement Required", cls: "bg-red-50 text-red-700 border-red-300" },
};
export const DOC_TYPES = ["business_licence", "tax_clearance", "brela_certificate", "tin_certificate", "vrn_certificate", "work_permit", "insurance_policy", "lease_agreement", "permit", "contract", "other"];
export const PRIORITY_CLS = { low: "text-slate-500", normal: "text-slate-700", high: "text-amber-700 font-semibold", urgent: "text-red-600 font-bold" };
export const ASSIST_STATUS = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
  quoted: { label: "Fee sent to client", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  quote_accepted: { label: "Fee accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  quote_declined: { label: "Fee declined", cls: "bg-red-50 text-red-700 border-red-200" },
};

export const APPLICABILITY = {
  applicable: { label: "Applicable", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  not_applicable: { label: "Not Applicable", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  pending: { label: "Pending Assessment", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export const REQUEST_STATUS = {
  open: { label: "Open", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "In Progress", cls: "bg-teal-50 text-teal-800 border-teal-200" },
  awaiting_client: { label: "Awaiting Client", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  quoted: { label: "Quote sent", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  declined: { label: "Declined", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

export const INVOICE_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  issued: { label: "Issued", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  partially_paid: { label: "Partially Paid", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  overdue: { label: "Overdue", cls: "bg-red-50 text-red-700 border-red-300" },
  void: { label: "Void", cls: "bg-gray-100 text-gray-500 border-gray-200 line-through" },
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paused: { label: "Paused", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  cancelled: { label: "Cancelled", cls: "bg-gray-100 text-gray-500 border-gray-200" },
  recorded: { label: "Recorded (manual)", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  reversed: { label: "Reversed", cls: "bg-red-50 text-red-700 border-red-200 line-through" },
};
export const PAY_CHANNELS = ["bank_transfer", "mobile_money", "cash", "cheque", "other"];
export const BILLING_PERIODS = ["monthly", "quarterly", "semi_annual", "annual", "one_off"];
export const tzs = (n) => `TZS ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ROLE_LABEL = { admin: "System Admin", staff: "V.A Staff", client_owner: "Client Owner", client_contributor: "Contributor" };
export const MGMT_LABEL = { va_managed: "V.A-managed", self_managed: "Self-managed", hybrid: "Hybrid" };
export const DOC_CATEGORIES = ["tax", "legal", "payroll", "licences", "financial", "corporate", "evidence", "other"];
export const REQUEST_TYPES = ["tax_filing", "registration", "licence", "payroll", "advisory", "document_request", "nssf", "wcf", "bot_reports", "osha", "service_levy", "other"];
export const TEMPLATE_CATEGORIES = ["tax", "payroll", "corporate", "licensing", "social_security", "other"];

export const CLIENT_TRANSITIONS = { not_started: ["in_progress"], in_progress: ["submitted"], with_authority: ["in_progress", "submitted"], awaiting_client: ["in_progress", "submitted"], returned: ["in_progress", "submitted"], submitted: ["verified", "returned"] };
export const CLIENT_VA_TRANSITIONS = { awaiting_client: ["in_progress"] };
export const STAFF_TRANSITIONS = {
  not_started: ["in_progress", "awaiting_client"],
  in_progress: ["not_started", "awaiting_client", "submitted"],
  with_authority: ["in_progress", "awaiting_client", "submitted"],
  awaiting_client: ["in_progress", "submitted"],
  submitted: ["under_review", "awaiting_client", "returned", "verified"],
  under_review: ["verified", "awaiting_client", "returned", "in_progress"],
  returned: ["in_progress", "awaiting_client", "submitted"],
  verified: [],
  cancelled: [],
};

export const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
// Dates are rendered in English and localized live by i18n/dom.js, so open pages follow the language switch.
const loc = () => "en-GB";
export const fmtDate = (d) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString(loc(), { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(loc(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
export const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const isStaffRole = (r) => r === "admin" || r === "staff";
